import { resolveImageUrl } from '../resolveImageUrl';
import type { PlaceObject } from '../types';

// FROZEN CROSS-FILE CONTRACT: the `chips` strings emitted here are parsed by
// substring in PlaceDetailModule (`.includes('Google reviews' / 'Open' /
// 'Closed' / '$')`) with no type guard. The exact string shapes
// (`⭐ {rating} ({reviewCount} Google reviews)`, status, priceLevel) must stay
// byte-identical — any whitespace/format drift silently breaks the detail view.
export const transformPlaceCard = (place: PlaceObject, card: any) => {
  // Build sections array, avoiding duplicates
  const sections: any[] = [];

  if (card.sections) {
    card.sections.forEach((section: any) => {
      sections.push({
        title: section.title,
        ...(section.body ? { body: section.body } : {}),
        items: Array.isArray(section.items) ? section.items.map((item: string) => ({ value: item })) : []
      });
    });
  }

  // If no About section exists but we have a description, add one
  // Prefer the longer description (AI-generated) over Google's short one
  const bestDescription = (place.description && place.description.length > (card.description?.length || 0))
    ? place.description : card.description;
  const hasAboutSection = sections.some((s: any) => s.title === 'About');
  if (!hasAboutSection && bestDescription) {
    sections.unshift({ title: 'About', body: bestDescription, items: [] });
  }
  // Also update existing About section if DB description is longer
  if (hasAboutSection && place.description) {
    const aboutSection = sections.find((s: any) => s.title === 'About');
    if (aboutSection && place.description.length > (aboutSection.body?.length || 0)) {
      aboutSection.body = place.description;
    }
  }

  // Only add Address section if it's not already in card.sections
  const hasAddressSection = card.sections?.some((s: any) => s.title === 'Address');
  if (card.address && !hasAddressSection) {
    sections.push({
      title: 'Address',
      items: [{ value: card.address }]
    });
  }

  // Only add Phone section if it's not already in card.sections
  const hasPhoneSection = card.sections?.some((s: any) => s.title === 'Phone');
  if (card.phoneNumber && !hasPhoneSection) {
    sections.push({
      title: 'Phone',
      items: [{ value: card.phoneNumber }]
    });
  }

  return {
    objectId: place.id,
    etag: place._etag,
    placeId: card.placeId,
    title: card.title,
    subtitle: card.subtitle || card.address,
    chips: [
      card.rating && card.reviewCount ? `⭐ ${card.rating} (${card.reviewCount} Google reviews)` : null,
      card.status,
      card.priceLevel
    ].filter(Boolean),
    images: card.images
      ?.filter((img: any) => !img.isMap)
      .map((img: any) => ({
        fullsizeUrl: resolveImageUrl(img.url),
        thumbnailUrl: resolveImageUrl(img.url),
        caption: img.attribution,
      })) || [],
    sections: sections,
    actions: [
      card.websiteUri ? {
        kind: 'link',
        url: card.websiteUri,
        label: 'Website'
      } : null,
      card.googleMapsUri ? {
        kind: 'map',
        url: card.googleMapsUri,
        label: 'Directions'
      } : null,
      card.phoneNumber ? {
        kind: 'call',
        url: `tel:${card.phoneNumber}`,
        label: 'Call'
      } : null
    ].filter(Boolean),
    galleryPhotos: card.images
      ?.filter((img: any) => !img.isMap)
      .map((img: any) => ({
        fullsizeUrl: resolveImageUrl(img.url),
        thumbnailUrl: resolveImageUrl(img.url),
        caption: img.attribution
      })) || [],
    userNotes: place.userNotes || '',
    userRating: place.userRating || undefined,
    imageUrl: place.imageUrl || ''
  };
};
